import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import { useAuth } from "../../contexts/AuthContext";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import { CONSOLE_DAYS, CONSOLE_SLOTS, getCityFromSearchParams, ANCHOR_SLOTS, GAP_SEARCH_IDEAS } from "../../utils/consoleUtils";
import "../../styles/console.css";

const EXPANSION_PLAYBOOK_STEPS = [
  { n: 1, title: "Establish Anchor Runs", detail: "Sat AM, Sun AM, Mon PM. Fill these three first." },
  { n: 2, title: "Fill the week", detail: "Add runs so every day has at least AM or PM covered." },
  { n: 3, title: "Add density", detail: "Second run per slot for stability. Multiple clubs per slot." },
  { n: 4, title: "Verify and publish", detail: "Complete location, notes, pace. Then publish so runs count toward coverage." },
  { n: 5, title: "Stable city", detail: "30+ reliable runs. City maintains itself. Focus energy on next city." },
];

const REL_OPTIONS = [
  { value: "unverified", label: "? Unverified" },
  { value: "regular", label: "✓ Regular" },
  { value: "occasional", label: "~ Occasional" },
  { value: "anchor", label: "⚓ Anchor" },
];

function nextDateForDayAndTime(dayLabel, timeStr) {
  const dayIdx = CONSOLE_DAYS.indexOf(dayLabel);
  if (dayIdx === -1) return null;
  if (timeStr == null || typeof timeStr !== "string" || !timeStr.trim()) return null;
  const timeMatch = timeStr.trim().match(/(\d+):?(\d*)/);
  if (!timeMatch) return null;
  const now = new Date();
  const currentDay = now.getDay();
  const targetJsDay = dayIdx === 6 ? 0 : dayIdx + 1;
  let daysAhead = targetJsDay - currentDay;
  if (daysAhead <= 0) daysAhead += 7;
  const d = new Date(now);
  d.setDate(d.getDate() + daysAhead);
  const hour = parseInt(timeMatch[1], 10);
  const min = timeMatch[2] !== "" ? parseInt(timeMatch[2], 10) : 0;
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
}


export default function ExpansionView() {
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const { user } = useAuth();
  const [quickName, setQuickName] = useState("");
  const [quickClubId, setQuickClubId] = useState("");
  const [quickDay, setQuickDay] = useState("");
  const [quickSlot, setQuickSlot] = useState("");
  const [quickTime, setQuickTime] = useState("6:30");
  const [quickRel, setQuickRel] = useState("regular");
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickError, setQuickError] = useState(null);

  const { data: coverageData, loading: coverageLoading, error: coverageError } = useConsoleFetch(
    () => (cityId ? consoleApi.stats.coverage(cityId) : Promise.resolve(null)),
    [cityId]
  );
  const { data: clubsData } = useConsoleFetch(
    () => (cityId ? consoleApi.clubs.list({ city_id: cityId, limit: 200 }) : Promise.resolve([])),
    [cityId]
  );
  const clubs = Array.isArray(clubsData) ? clubsData : [];

  const slotMap = coverageData?.slot_map ?? [];
  const gaps = coverageData?.gaps ?? [];
  const cityName = coverageData?.city_name ?? "";
  const coveredSlots = Array.isArray(slotMap) ? slotMap.filter((s) => typeof s?.count === "number" && s.count > 0).length : 0;

  const getSlot = useCallback(
    (day, slot) => slotMap.find((s) => s.day === day && s.slot === slot),
    [slotMap]
  );

  /** Top gaps for Missing Slots panel (empty first, then fragile). */
  const missingSlotsForTargets = gaps.slice(0, 8);

  const handleQuickAdd = useCallback(async () => {
    if (!quickName.trim() || !quickClubId || !quickDay || !quickSlot || !user?.id) return;
    setQuickError(null);
    setQuickSubmitting(true);
    try {
      const scheduledDate = nextDateForDayAndTime(quickDay, quickTime);
      if (!scheduledDate) throw new Error("Invalid day/time");
      await consoleApi.meetings.create({
        club_id: quickClubId,
        name: quickName.trim(),
        scheduled_date: scheduledDate,
        address: "TBD",
        created_by: user.id,
        reliability: quickRel,
        slot: quickSlot,
        status: "draft",
        is_public: true,
      });
      setQuickName("");
      setQuickClubId("");
      setQuickDay("");
      setQuickSlot("");
      setQuickTime("6:30");
      setQuickRel("regular");
    } catch (e) {
      setQuickError(e.response?.data?.detail || e.message || "Failed to add run");
    } finally {
      setQuickSubmitting(false);
    }
  }, [quickName, quickClubId, quickDay, quickSlot, quickTime, quickRel, user?.id]);

  return (
    <ConsoleViewShell
      loading={coverageLoading}
      error={coverageError}
      loadingLabel="Loading expansion…"
    >
      {!cityId && (
        <div className="console-view">
          <p className="console-muted-text">Select a city to view expansion.</p>
        </div>
      )}
      {cityId && coverageData && (
        <div className="console-main-inner console-expansion">
          <ConsolePageHeader
            label="City Seeding"
            title="Expansion"
            sub="Build the weekly rhythm. Seed the three anchor runs first, then fill the week. All data from live coverage API."
          />
          <div className="exp-hero">
            <div className="exp-hero-eyebrow">City Expansion Engine</div>
            <h2 className="exp-hero-title">Build the Weekly Rhythm</h2>
            <p className="exp-hero-sub">
              Seed the three anchor runs first. Then fill the week. Then add density.
            </p>
            <div className="exp-workflow">
              <span className="exp-workflow-label">Workflow</span>
              <span className="exp-workflow-step">Research club</span>
              <span className="exp-workflow-arrow">→</span>
              <span className="exp-workflow-step">+ Add Club</span>
              <span className="exp-workflow-arrow">→</span>
              <span className="exp-workflow-step">Create drafts</span>
              <span className="exp-workflow-arrow">→</span>
              <span className="exp-workflow-step exp-workflow-step-active">Review</span>
              <span className="exp-workflow-arrow">→</span>
              <span className="exp-workflow-step">Publish</span>
            </div>
          </div>

          <div className="exp-layout">
            <aside className="exp-sidebar">
              <div className="console-ops-card">
                <div className="console-ops-kicker">Active City</div>
                <div className="exp-city-badge">
                  <span className="exp-city-name">{coverageData.city_name ?? ""}</span>
                  <span className="exp-city-status">{coverageData.phase ?? ""}</span>
                </div>
              </div>

              <div className="console-ops-card">
                <div className="console-ops-kicker">The Three-Run Rule</div>
                <p className="exp-three-desc">
                  A city becomes real when these three anchor runs exist. Fill these first.
                </p>
                <div className="exp-three-grid">
                  {ANCHOR_SLOTS.map(({ day, slot }) => {
                    const cell = getSlot(day, slot);
                    const filled = cell && typeof cell.count === "number" && cell.count > 0;
                    return (
                      <div
                        key={`${day}-${slot}`}
                        className={`exp-three-card ${filled ? "filled" : "missing"}`}
                      >
                        <span className="exp-three-slot">{day} {slot.toUpperCase()}</span>
                        <span className="exp-three-name">
                          {filled ? (cell.runs?.[0]?.name ?? "") : "Empty"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </aside>

            <div className="exp-workspace">
              <div className="console-ops-card">
                <div className="console-ops-kicker">Coverage at a glance</div>
                <div className="exp-week-header">
                  <span className="exp-week-title">
                    {coveredSlots >= 12 ? "Strong weekly rhythm" : "Building the weekly rhythm"}
                  </span>
                  <span className="exp-week-pill">{coveredSlots} / 14</span>
                </div>
                <div className="exp-week-grid">
                  {CONSOLE_DAYS.map((day) => (
                    <div key={day} className="exp-week-col">
                      <div className="exp-week-day">{day}</div>
                      {CONSOLE_SLOTS.map((slot) => {
                        const cell = getSlot(day, slot);
                        const hasRun = cell && typeof cell.count === "number" && cell.count > 0;
                        return (
                          <div
                            key={`${day}-${slot}`}
                            className={`exp-week-slot ${hasRun ? "has-run" : "empty"}`}
                          >
                            {hasRun ? (cell.runs?.[0]?.name ?? "") : ""}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Missing Slots — Search Targets (v18) */}
              <div className="console-ops-card">
                <div className="console-ops-kicker">Missing Slots — Search Targets</div>
                {missingSlotsForTargets.length === 0 ? (
                  <p className="console-doctrine-p exp-no-gaps-msg">
                    No gaps. All 14 slots have at least one run.
                  </p>
                ) : (
                  <div className="exp-search-prompt-grid">
                    {missingSlotsForTargets.map((g) => {
                      const label = `${g.day} ${(g.slot ?? "").toUpperCase()}`.trim();
                      const fallback = `${label} run club ${cityName}`;
                      const firstQuery = (GAP_SEARCH_IDEAS[label]?.[0] ?? fallback).replace(/\{city\}/g, cityName);
                      return (
                        <div key={label} className="exp-search-prompt-card">
                          <div className="exp-search-prompt-slot">{label}</div>
                          <div className="exp-search-prompt-query">{firstQuery}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="console-ops-card">
                <div className="console-ops-kicker">Quick Add Run</div>
                <div className="exp-quick-add">
                  <input
                    type="text"
                    placeholder="Run name"
                    value={quickName}
                    onChange={(e) => setQuickName(e.target.value)}
                    className="exp-quick-input"
                  />
                  <select
                    value={quickClubId}
                    onChange={(e) => setQuickClubId(e.target.value)}
                    className="exp-quick-select"
                  >
                    <option value="">Club…</option>
                    {clubs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={quickDay}
                    onChange={(e) => setQuickDay(e.target.value)}
                    className="exp-quick-select"
                  >
                    <option value="">Day</option>
                    {CONSOLE_DAYS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={quickSlot}
                    onChange={(e) => setQuickSlot(e.target.value)}
                    className="exp-quick-select"
                  >
                    <option value="">AM/PM</option>
                    <option value="am">AM</option>
                    <option value="pm">PM</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Time"
                    value={quickTime}
                    onChange={(e) => setQuickTime(e.target.value)}
                    className="exp-quick-time"
                  />
                  <select
                    value={quickRel}
                    onChange={(e) => setQuickRel(e.target.value)}
                    className="exp-quick-select"
                  >
                    {REL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="exp-quick-btn"
                    onClick={handleQuickAdd}
                    disabled={quickSubmitting || !quickName.trim() || !quickClubId || !quickDay || !quickSlot || !user?.id}
                  >
                    {quickSubmitting ? "Adding…" : "Add"}
                  </button>
                </div>
                {quickError && <p className="exp-quick-err">{quickError}</p>}
              </div>

              <div className="console-ops-card">
                <div className="console-ops-kicker">Expansion Playbook</div>
                <p className="console-doctrine-p exp-playbook-intro">
                  Structure before density. Anchor before redundancy. The correct seeding order is the same for every city.
                </p>
                <ol className="exp-playbook-steps">
                  {EXPANSION_PLAYBOOK_STEPS.map((step) => (
                    <li key={step.n} className="exp-playbook-step">
                      <strong>Stage {step.n}</strong> — {step.title}: {step.detail}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConsoleViewShell>
  );
}
